'use client';

import { cn, Input } from '@shared/components/shadcn';
import { forwardRef } from 'react';
import { FieldInfo } from '../FieldInfo/FieldInfo';
import { FieldMessage } from '../FieldMessage/FieldMessage';
import type { TextInputProps } from '../types';

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  {
    label,
    topActionElement,
    leftIcon,
    rightIcon,
    helpMessage,
    helpMessageMode = 'inline',
    helpMessageType,
    bottomActionElement,
    className,
    inputTrackingProps,
    helperTrackingProps,
    ...props
  }: TextInputProps,
  ref
) {
  // Ensure value is never NaN to prevent React warnings
  const safeInputProps = {
    ...props,
    value: props.value === undefined || props.value === null || (typeof props.value === 'number' && isNaN(props.value)) ? '' : props.value,
  };
  const classes = {
    wrapper: 'w-full relative paragraph',
    field: 'peer/text-input relative', // Ensures icons are absolutely positioned to the input, not the wrapper.
    icon: (position: 'left' | 'right') => `absolute top-1/2 -translate-y-1/2 paragraph-strong flex items-center ${position === 'left' ? 'left-4' : 'right-4'}`,
    input: cn(
      'placeholder:text-foreground/60 placeholder:paragraph ',
      helpMessageType === 'error' && 'border-error-foreground',
      leftIcon && 'pl-8', // Standard left padding when there's a left icon
      rightIcon && 'pr-8' // Standard right padding when there's a right icon
    ),
    message: cn(
      helpMessageMode === 'inline-foreground' && 'peer-focus-within/text-input:**:text-foreground',
      helpMessageMode === 'focusout' && 'peer-focus-within/text-input:hidden'
    ),
  };

  return (
    <div className={cn(classes.wrapper, className)}>
      <FieldInfo label={label} htmlFor={safeInputProps.id} actionElement={topActionElement} disabled={safeInputProps.disabled} />
      <div className={classes.field}>
        {leftIcon && <span className={classes.icon('left')}>{leftIcon}</span>}
        <Input
          aria-describedby={helpMessage ? `${safeInputProps.id}-message` : undefined}
          aria-invalid={helpMessageType === 'error' || undefined}
          className={classes.input}
          {...inputTrackingProps}
          {...safeInputProps}
          ref={ref}
        />
        {rightIcon && <span className={classes.icon('right')}>{rightIcon}</span>}
      </div>
      <FieldMessage
        id={`${safeInputProps.id}-message`}
        className={classes.message}
        message={helpMessage}
        messageType={helpMessageType}
        actionElement={bottomActionElement}
        disabled={safeInputProps.disabled}
        {...helperTrackingProps}
        data-testid={`${safeInputProps.id}-error`}
      />
    </div>
  );
});

export { TextInput };
